import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import { IContestAward } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { TrophyIcon } from '../CustomSVGIcons/TrophyIcon';
import CalendarIcon from '../CustomSVGIcons/CalendarIcon';

import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { formatDate } from '../../utils/AppUtils';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';

export interface IWinnerVerticalProject {
    award: IContestAward;
}

const PersonCard: React.FC<IWinnerVerticalProject> = ({ award }) => {
    const dispatch = useDispatch();
    const routes = replaceRouteParam(ROUTES.PROJECT_DETAILS, 'slug', award.award_project.video_id);
    const { themeStyle } = useThemeContext();
    const [isHovered, setIsHovered] = useState(false);

    const handleNavigation = () => {
        dispatch(
            navigate({
                routes,
                openInNewTab: true,
            }),
        );
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'won':
                return 'success';
            case 'nominated':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        if (status === 'won') {
            return <TrophyIcon style={{ width: '12px', height: '12px' }} />;
        }
        return undefined;
    };

    const getAwardIcon = (status: string) => {
        if (status === 'won') {
            return <TrophyIcon style={{ width: '24px', height: '24px' }} />;
        }
        return null;
    };

    return (
        <Card
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                width: '100%',
                maxWidth: '400px',
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: theme[themeStyle].secondaryBg,
                transition: 'all 0.3s ease',
                transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                boxShadow: isHovered ? '0 8px 25px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
            }}>
            {/* Thumbnail/Feature Image Section */}
            <Box
                onClick={handleNavigation}
                sx={{
                    position: 'relative',
                    aspectRatio: '16/9',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    overflow: 'hidden',
                }}>
                <img
                    src={award.feature_image ?? award.award_project.feature_image ?? award.award_project.thumbnail}
                    alt={award.award_project.title}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    }}
                />

                {/* Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        transition: 'background-color 0.3s ease',
                        ...(isHovered && { backgroundColor: 'rgba(0,0,0,0.3)' }),
                    }}
                />

                {/* Status Badge - Top Left */}
                <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
                    <Chip
                        icon={getStatusIcon(award.award_status)}
                        label={award.award_status.charAt(0).toUpperCase() + award.award_status.slice(1)}
                        color={getStatusColor(award.award_status)}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                    />
                </Box>

                {/* Year Badge - Top Right */}
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                    <Chip
                        icon={<CalendarIcon style={{ width: '12px', height: '12px' }} />}
                        label={formatDate(award.award_year, 'MMM YYYY')}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                    />
                </Box>

                {/* Award Icon - Center (for won status) */}
                {award.award_status === 'won' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255, 193, 7, 0.9)',
                                borderRadius: '50%',
                                padding: 1.5,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                transition: 'transform 0.3s ease',
                                ...(isHovered && { transform: 'scale(1.1)' }),
                            }}>
                            {getAwardIcon(award.award_status)}
                        </Box>
                    </Box>
                )}

                {/* Category Badge - Bottom Left */}
                <Box sx={{ position: 'absolute', bottom: 12, left: 12 }}>
                    <Chip
                        label={award.award_category.category_title}
                        size="small"
                        variant="outlined"
                        sx={{
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: theme[themeStyle].true_white,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                    />
                </Box>
            </Box>

            {/* Content Section */}
            <CardContent sx={{ padding: 3 }}>
                {/* Project Title */}
                <Typography
                    variant="h6"
                    onClick={handleNavigation}
                    sx={{
                        fontWeight: 'bold',
                        color: theme[themeStyle].tag_white,
                        marginBottom: 2,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.125rem',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        transition: 'color 0.2s ease',
                        ...(isHovered && { color: theme[themeStyle].primary_orange }),
                    }}>
                    {award.award_project.title}
                </Typography>

                {/* Winner Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                    <ThspianAvatar user={award.person_awarded} enableDefaultNavigation />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {award.person_awarded.firstName} {award.person_awarded.lastName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].dropdown_text,
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            @{award.person_awarded.userName}
                        </Typography>
                    </Box>
                </Box>

                {/* Contest Info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: '0.75rem',
                        }}>
                        {award.contest.title}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme[themeStyle].dropdown_text,
                            fontSize: '0.75rem',
                        }}>
                        {award.award_category.festival}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export { PersonCard };
